import { useQuery } from '@tanstack/react-query';
import { Loader2, Building2, MapPin, Shield, TrendingUp, DollarSign, BarChart3, Database } from 'lucide-react';
import { SectionTitle, GoldBadge } from '../components/Luxury';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MarketDbStats {
  property_count: number;
  avg_cap_rate: number | null;
  median_rent: number | null;
  rent_coverage: number;
}

interface MarketComparisonData {
  market_id: string;
  display_name: string;
  state: string;
  state_full: string;
  submarket_count: number;
  effective_tax_rate: number;
  property_tax_rate: number;
  transfer_tax_total: number;
  capital_gains_rate: number;
  state_capital_gains_rate: number;
  tier_opex_rates: Record<string, number>;
  vacancy_rate_by_tier: Record<string, number>;
  rent_growth_rates: Record<string, number>;
  appreciation_rates: Record<string, number>;
  cap_rate_thresholds: Record<string, Record<string, number>>;
  capex_per_unit: Record<string, number>;
  assessor_name: string;
  permit_source_name: string;
  db_stats: MarketDbStats;
}

// ---------------------------------------------------------------------------
// Helper: Determine "best" value across markets for highlighting
// ---------------------------------------------------------------------------

type Direction = 'low' | 'high';

function getBestMarketId(
  markets: MarketComparisonData[],
  accessor: (m: MarketComparisonData) => number | null | undefined,
  direction: Direction,
): string | null {
  let bestId: string | null = null;
  let bestVal: number | null = null;
  for (const m of markets) {
    const val = accessor(m);
    if (val == null) continue;
    if (
      bestVal === null ||
      (direction === 'low' && val < bestVal) ||
      (direction === 'high' && val > bestVal)
    ) {
      bestVal = val;
      bestId = m.market_id;
    }
  }
  return bestId;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HighlightCell({
  value,
  isBest,
  suffix = '',
  prefix = '',
}: {
  value: string;
  isBest: boolean;
  suffix?: string;
  prefix?: string;
}) {
  return (
    <span
      className={`font-mono text-sm ${
        isBest ? 'text-emerald font-semibold' : 'text-ivory'
      }`}
    >
      {prefix}{value}{suffix}
    </span>
  );
}

function StatRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-dark/50 last:border-b-0">
      <span className="text-stone-500 text-xs">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function TierRow({
  tier,
  value,
  isBest,
  format,
}: {
  tier: string;
  value: number;
  isBest: boolean;
  format: 'pct' | 'dollar';
}) {
  const tierColors: Record<string, string> = {
    Tier1: 'text-tier1',
    Tier2: 'text-tier2',
    Tier3: 'text-tier3',
  };
  const display =
    format === 'pct'
      ? `${(value * 100).toFixed(1)}%`
      : `$${value.toLocaleString()}`;
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`text-xs font-medium ${tierColors[tier] || 'text-stone-500'}`}>
        {tier}
      </span>
      <span
        className={`font-mono text-xs ${
          isBest ? 'text-emerald font-semibold' : 'text-ivory'
        }`}
      >
        {display}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Market Card
// ---------------------------------------------------------------------------

function MarketCard({
  market,
  allMarkets,
}: {
  market: MarketComparisonData;
  allMarkets: MarketComparisonData[];
}) {
  const bestEffTax = getBestMarketId(allMarkets, (m) => m.effective_tax_rate, 'low');
  const bestTransferTax = getBestMarketId(allMarkets, (m) => m.transfer_tax_total, 'low');
  const bestPropertyTax = getBestMarketId(allMarkets, (m) => m.property_tax_rate, 'low');

  const bestPropertyCount = getBestMarketId(allMarkets, (m) => m.db_stats.property_count, 'high');

  const hasZandi = market.submarket_count > 0;
  const stats = market.db_stats;

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 bg-graphite border-b border-slate-dark">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gold" />
            <h3 className="text-lg font-semibold text-ivory">
              {market.display_name}
            </h3>
          </div>
          <span className="text-xs font-medium text-stone-500 bg-slate-dark/60 px-2 py-0.5 rounded">
            {market.state}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasZandi ? (
            <GoldBadge variant="gold">Zandi Scoring</GoldBadge>
          ) : (
            <span className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-stone-500 border border-slate-dark bg-transparent">
              Config Only
            </span>
          )}
          {hasZandi && (
            <span className="text-[10px] text-stone-500">
              {market.submarket_count} submarkets
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 flex-1 space-y-5">
        {/* Tax Rates */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Tax Rates
          </h4>
          <StatRow label="Effective Income Tax">
            <HighlightCell
              value={(market.effective_tax_rate * 100).toFixed(1)}
              isBest={bestEffTax === market.market_id}
              suffix="%"
            />
          </StatRow>
          <StatRow label="Property Tax">
            <HighlightCell
              value={(market.property_tax_rate * 100).toFixed(3)}
              isBest={bestPropertyTax === market.market_id}
              suffix="%"
            />
          </StatRow>
          <StatRow label="Transfer Tax">
            <HighlightCell
              value={(market.transfer_tax_total * 100).toFixed(2)}
              isBest={bestTransferTax === market.market_id}
              suffix="%"
            />
          </StatRow>
          <StatRow label="Capital Gains (Fed)">
            <span className="font-mono text-sm text-ivory">
              {(market.capital_gains_rate * 100).toFixed(1)}%
            </span>
          </StatRow>
          <StatRow label="Capital Gains (State)">
            <span className="font-mono text-sm text-ivory">
              {(market.state_capital_gains_rate * 100).toFixed(2)}%
            </span>
          </StatRow>
        </div>

        {/* Operating Assumptions (Tier2 as representative) */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            Operating Assumptions
          </h4>
          <div className="space-y-0.5">
            <div className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">OpEx Ratio by Tier</div>
            {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
              const bestForTier = getBestMarketId(allMarkets, (m) => m.tier_opex_rates[tier], 'low');
              return (
                <TierRow
                  key={tier}
                  tier={tier}
                  value={market.tier_opex_rates[tier]}
                  isBest={bestForTier === market.market_id}
                  format="pct"
                />
              );
            })}
          </div>
          <div className="mt-3 space-y-0.5">
            <div className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Vacancy by Tier</div>
            {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
              const bestForTier = getBestMarketId(allMarkets, (m) => m.vacancy_rate_by_tier[tier], 'low');
              return (
                <TierRow
                  key={tier}
                  tier={tier}
                  value={market.vacancy_rate_by_tier[tier]}
                  isBest={bestForTier === market.market_id}
                  format="pct"
                />
              );
            })}
          </div>
        </div>

        {/* Growth & Appreciation */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            Growth & Appreciation
          </h4>
          <div className="space-y-0.5">
            <div className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Rent Growth by Tier</div>
            {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
              const bestForTier = getBestMarketId(allMarkets, (m) => m.rent_growth_rates[tier], 'high');
              return (
                <TierRow
                  key={tier}
                  tier={tier}
                  value={market.rent_growth_rates[tier]}
                  isBest={bestForTier === market.market_id}
                  format="pct"
                />
              );
            })}
          </div>
          <div className="mt-3 space-y-0.5">
            <div className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Appreciation by Tier</div>
            {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
              const bestForTier = getBestMarketId(allMarkets, (m) => m.appreciation_rates[tier], 'high');
              return (
                <TierRow
                  key={tier}
                  tier={tier}
                  value={market.appreciation_rates[tier]}
                  isBest={bestForTier === market.market_id}
                  format="pct"
                />
              );
            })}
          </div>
        </div>

        {/* CapEx per Unit */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" />
            CapEx per Unit
          </h4>
          <div className="space-y-0.5">
            {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
              const bestForTier = getBestMarketId(allMarkets, (m) => m.capex_per_unit[tier], 'low');
              return (
                <TierRow
                  key={tier}
                  tier={tier}
                  value={market.capex_per_unit[tier]}
                  isBest={bestForTier === market.market_id}
                  format="dollar"
                />
              );
            })}
          </div>
        </div>

        {/* Cap Rate Thresholds */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            Cap Rate Thresholds
          </h4>
          {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
            const thresholds = market.cap_rate_thresholds[tier];
            if (!thresholds) return null;
            const tierColors: Record<string, string> = {
              Tier1: 'text-tier1',
              Tier2: 'text-tier2',
              Tier3: 'text-tier3',
            };
            return (
              <div key={tier} className="flex items-center justify-between py-1 border-b border-slate-dark/30 last:border-b-0">
                <span className={`text-xs font-medium ${tierColors[tier]}`}>{tier}</span>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-stone-500">
                    Med <span className="text-ivory">{thresholds.medium}</span>
                  </span>
                  <span className="text-stone-500">
                    Str <span className="text-emerald">{thresholds.strong}</span>
                  </span>
                  <span className="text-stone-500">
                    Ideal <span className="text-gold">{thresholds.ideal}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Live Deal Stats */}
        <div>
          <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" />
            Live Deal Stats
          </h4>
          {stats.property_count > 0 ? (
            <>
              <StatRow label="Properties in DB">
                <HighlightCell
                  value={stats.property_count.toLocaleString()}
                  isBest={bestPropertyCount === market.market_id}
                />
              </StatRow>
              <StatRow label="Avg Cap Rate">
                <span className="font-mono text-sm text-ivory">
                  {stats.avg_cap_rate != null
                    ? `${(stats.avg_cap_rate * 100).toFixed(2)}%`
                    : '--'}
                </span>
              </StatRow>
              <StatRow label="Median Rent">
                <span className="font-mono text-sm text-ivory">
                  {stats.median_rent != null
                    ? `$${stats.median_rent.toLocaleString()}/mo`
                    : '--'}
                </span>
              </StatRow>
              <StatRow label="Rent Coverage">
                <span className="font-mono text-sm text-ivory">
                  {(stats.rent_coverage * 100).toFixed(0)}%
                </span>
              </StatRow>
            </>
          ) : (
            <div className="text-center py-4">
              <Building2 className="w-6 h-6 text-stone-400 mx-auto mb-2" />
              <p className="text-xs text-stone-500">No properties sourced</p>
            </div>
          )}
        </div>

        {/* Data Sources */}
        <div className="pt-2 border-t border-slate-dark">
          <div className="flex items-center justify-between text-[10px] text-stone-400">
            <span>Assessor: {market.assessor_name || '--'}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-stone-400 mt-0.5">
            <span>Permits: {market.permit_source_name || '--'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function MarketComparison() {
  const { data, isLoading, error } = useQuery<MarketComparisonData[]>({
    queryKey: ['market-comparison'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/markets/comparison`);
      if (!response.ok) {
        throw new Error(`Failed to load market comparison: ${response.statusText}`);
      }
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-obsidian">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-stone-500">Loading market comparison...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-obsidian">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-crimson mx-auto mb-4" />
          <p className="text-ivory text-lg mb-2">Failed to load comparison</p>
          <p className="text-stone-500 text-sm">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const markets = data || [];

  return (
    <div className="flex flex-col h-full bg-obsidian">
      {/* Header */}
      <div className="flex-shrink-0 bg-charcoal border-b border-slate-dark px-4 py-6 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionTitle icon={<BarChart3 className="w-5 h-5" />}>
            Cross-Market Comparison
          </SectionTitle>
          <p className="text-stone-500 text-sm -mt-4">
            Compare config assumptions and live deal stats across all active markets.
            Best values per metric are highlighted in{' '}
            <span className="text-emerald font-medium">green</span>.
          </p>
        </div>
      </div>

      {/* Market Cards Grid */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {markets.map((market) => (
              <MarketCard
                key={market.market_id}
                market={market}
                allMarkets={markets}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
